import type { LeagueKey } from '@parlay-picker/shared/league';
import { getTeamInfo } from '@parlay-picker/shared/teams';
import { type ReactElement, useEffect, useState } from 'react';
import { TeamLogo } from '../TeamLogo';

/** Countdown timer for pre-game */
function CountdownTimer({ gameStartsAt }: { gameStartsAt: string }): ReactElement {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const startMs = new Date(gameStartsAt).getTime();
  const diff = startMs - now;

  if (diff <= 0) {
    return <span className="text-theme-warning text-sm font-medium">Starting soon</span>;
  }

  const hours = Math.floor(diff / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  const seconds = Math.floor((diff % 60_000) / 1000);

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  parts.push(`${seconds}s`);

  return <span className="text-sm font-mono text-theme-text-secondary">{parts.join(' ')}</span>;
}

/** Pre-game matchup view when no live data is available */
export function PreGameView({
  homeTeam,
  awayTeam,
  gameStartsAt,
  leagueKey,
}: {
  homeTeam: string;
  awayTeam: string;
  gameStartsAt?: string;
  leagueKey: LeagueKey;
}): ReactElement {
  const startTime = gameStartsAt
    ? new Date(gameStartsAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : null;

  return (
    <div className="py-8 flex flex-col items-center gap-4">
      <div className="flex items-center gap-6">
        <div className="flex flex-col items-center gap-1.5">
          <TeamLogo name={awayTeam} leagueKey={leagueKey} size="lg" />
          <span className="text-sm font-semibold text-white">
            {getTeamInfo(awayTeam, leagueKey)?.abbr ?? awayTeam}
          </span>
        </div>

        <div className="flex flex-col items-center gap-1">
          <span className="text-theme-text-tertiary text-xs uppercase tracking-wider">vs</span>
          {startTime && <span className="text-lg font-mono font-bold text-white">{startTime}</span>}
        </div>

        <div className="flex flex-col items-center gap-1.5">
          <TeamLogo name={homeTeam} leagueKey={leagueKey} size="lg" />
          <span className="text-sm font-semibold text-white">
            {getTeamInfo(homeTeam, leagueKey)?.abbr ?? homeTeam}
          </span>
        </div>
      </div>

      {gameStartsAt && (
        <div className="flex flex-col items-center gap-1">
          {new Date(gameStartsAt).getTime() > Date.now() ? (
            <>
              <span className="text-[10px] text-theme-text-tertiary uppercase tracking-wider">
                Starts in
              </span>
              <CountdownTimer gameStartsAt={gameStartsAt} />
            </>
          ) : (
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-theme-warning rounded-full animate-pulse" />
              <span className="text-xs text-theme-warning font-medium">Waiting for tip-off</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
